import S, { DataSignal } from 's-js';
import { lift } from 's-array';
import { AssertExtends } from 'lib/assertExtends';
import { notNullish } from 'lib/notNullish';
import UOM from 'lib/UOM';
import { zipMap } from 'lib/zipMap';

import { Domain } from 'data/models/Domain';
import { Organization } from 'data/models/Organization';
import { PilotJobOrder } from 'data/models/PilotJobOrder';
import { PortCall } from 'data/models/PortCall';

import { PCCache } from 'data/PCCache';

import { LocalizationContext } from 'common/models/LocalizationContext';

import { MetroMapMilestone, MetroMapMilestoneParams } from 'portcall/metromapframework/model';
import { NullableFeetInchesMetersEdit, DecimalPlaces } from 'portcall/feetinchesmetersedit/model';
import { PortCallScheduleHeader } from 'portcall/portcallschedule/header';

import { AddPortCallTabModel } from './model';

export interface AddPortCallPilotsTabParams {
    PortCall : PortCall;
    ScheduleHeader : PortCallScheduleHeader;
}

export type AddPortCallPilotsTab = AssertExtends<ReturnType<typeof AddPortCallPilotsTab>, AddPortCallTabModel>;
export function AddPortCallPilotsTab(
    data : PCCache,
    domain : Domain,
    Localization : LocalizationContext,
    metroMapMilestoneModel : (params : MetroMapMilestoneParams) => MetroMapMilestone,
    {
        PortCall,
        ScheduleHeader
    } : AddPortCallPilotsTabParams
) {
    const
        PilotAgencies = S(() => domain.Organizations().map(org => org.PilotAgency()).filter(notNullish)),
        IsTabAvailable = () => (domain.Settings()?.HasPilots() ?? false) && PilotAgencies().length > 0,
        PilotWasRejected = S.value(false),
        IsPilotRequired : DataSignal<boolean> = (val? : boolean) => {
            if (val === undefined) {
                return IsTabAvailable() && !PilotWasRejected();
            } else {
                PilotWasRejected(!val);
                return val;
            }
        },
        Template = S(() => {
            if (IsPilotRequired()) {
                const tmpl = new PilotJobOrder({
                    Id: 0,
                    BillToAgent: true
                });
                data.PilotJobOrders.attach(tmpl, [tmpl.PilotAgency]);
                return tmpl;
            } else {
                return null;
            }
        }),
        MovementSeries = lift(() => PortCall.MovementSeries()),
        PilotJobOrders =
            lift(MovementSeries.reduce(
                (pjos, pt) => pjos.concat(pt.DeparturePilotJobOrders()),
                () : PilotJobOrder[] => []
            ))
            .map(pjo => PilotJobOrderViewModel(pjo, metroMapMilestoneModel)),
        TugCompanies = lift(() => Template()?.PilotAgency()?.TugCompanies() ?? [])
            .filter(tug => tug.Active()),
        LineHandlers = lift(() => Template()?.PilotAgency()?.LineHandlingCompanies() ?? [])
            .filter(line => line.Active());

    // Construct PJOs
    S(() => {
        const tmpl = Template();
        if (!tmpl) return;

        zipMap(
            MovementSeries,
            () => MovementSeries().slice(1),
            (fromPt, toPt) => S(() => {
                // No PJOs leaving the port-departure point
                if (fromPt.IsPortDeparture()) return;

                const depthUOM = () => fromPt.PortCall()?.Port()?.PierDepthUOM() ?? UOM.Foot;

                // Make the PJO
                const pjo = new PilotJobOrder({
                    Id: 0,
                    DeepDraft: { UOM: S.sample(depthUOM) },
                    AirDraft: { UOM: S.sample(depthUOM ) }
                });
                pjo.FromPoint(fromPt);
                fromPt.DeparturePilotJobOrders.push(pjo);
                S.cleanup(() => fromPt.DeparturePilotJobOrders.remove(pjo));

                // Set ToPoint navigation prop
                S(() => pjo.ToPoint(toPt() ?? null));

                // Set PJO Start/End times from from and to points
                S(() => pjo.Start.Estimated(fromPt.Departure.Estimated()));
                S(() => toPt() && pjo.End.Estimated(toPt()!.Arrival.Estimated()));

                // Set draft UOMs
                S.on(depthUOM, () => {
                    if (depthUOM() !== pjo.AirDraft.UOM()) {
                        pjo.AirDraft.set(pjo.AirDraft.convert(depthUOM()));
                    }
                });
                S.on(depthUOM, () => {
                    if (depthUOM() !== pjo.DeepDraft.UOM()) {
                        pjo.DeepDraft.set(pjo.DeepDraft.convert(depthUOM()));
                    }
                });

                // Set various other PJO attributes from the template
                S(() => pjo.PilotAgencyId(tmpl.PilotAgencyId()));
                S(() => pjo.TugCompanyId(tmpl.TugCompanyId()));
                S(() => pjo.NumberOfTugs(tmpl.NumberOfTugs()));
                S(() => pjo.LineHandlingCompanyId(tmpl.LineHandlingCompanyId()));
                // LaborStartTime applies to the first PJO only.
                S(() => pjo.LaborStartTime(fromPt.IsPortArrival() ? tmpl.LaborStartTime() : null));
                S(() => pjo.BillToAgent(tmpl.BillToAgent()));
                // NOTE: BillToAddress and Remarks are free text fields, so we MIGHT wanna introduce
                // some kind of debounce for both of those as a client-side performance optimization.
                // For now, assuming that the total number of PJOs for any given PortCall is likely
                // to remain small and that the performance impact of copying the full text to every
                // PJO on each keystroke will be insignificant.
                S(() => pjo.BillToAddress(tmpl.BillToAddress()));
                S(() => pjo.Remarks(tmpl.Remarks()));
            })
        );
    });

    // Autofill a default value for the template PJO's BillToAddress.
    S(() => {
        const tmpl = Template();
        if (tmpl) {
            const org = PortCall.Agent()?.Organization();
            if (org) tmpl.BillToAddress(defaultBillToAddress(org));
        }
    });

    // Default the template PJO's PilotAgencyId to the first pilot agency in the domain.
    // NOTE: For now, we're assuming that the Domain has at most one pilot agency, so this is the ONLY way by
    // which we'll be setting the template PJO's PilotAgencyId. If we wanna support multiple pilot agencies
    // someday, we'll need extra logic and/or UI to select the pilot agency, in which case we may or may not
    // want to default to the first pilot agency like this.
    S(() => {
        const tmpl = Template();
        if (tmpl && PilotAgencies().length > 0) tmpl.PilotAgencyId(PilotAgencies()[0].Id());
    });

    return {
        ScheduleHeader,
        Localization,
        IsTabAvailable,
        IsPilotRequired,
        Template,
        PilotJobOrders,
        CanProceed: () => true,
        TugCompanies,
        LineHandlers
    };
}

const DECIMAL_PLACES : DecimalPlaces = { inches: 0, meters: 2 };
export type PilotJobOrderViewModel = ReturnType<typeof PilotJobOrderViewModel>;
function PilotJobOrderViewModel(
    pjo : PilotJobOrder,
    metroMapMilestoneModel : (params : MetroMapMilestoneParams) => MetroMapMilestone
) {
    const
        Milestone = S(() => {
            // ToPoint -> ArrivalEntity -> PreviousMilestoneDate to support cases where the FromPoint's
            // DepartureEntity isn't a milestone.
            const Date = pjo.ToPoint()?.ArrivalEntity()?.PreviousMilestoneDate() ?? null;
            return Date && metroMapMilestoneModel({ Date });
        }),
        DeepDraft = NullableFeetInchesMetersEdit({ backingMeasurement: pjo.DeepDraft, decimalPlaces: DECIMAL_PLACES }),
        AirDraft  = NullableFeetInchesMetersEdit({ backingMeasurement: pjo.AirDraft , decimalPlaces: DECIMAL_PLACES });

    return {
        Model: pjo,
        Milestone,
        DeepDraft,
        AirDraft,
    };
}

function defaultBillToAddress(org : Organization) {
    const addr = org.PostalAddresses().length > 0 ? org.PostalAddresses()[0] : null;
    return [
        org.Name(),
        addr && `${addr.Address()}${addr.AptSte() ? ` ${addr.AptSte()}` : ''}`,
        addr && `${addr.City()}, ${addr.StateProv()} ${addr.PostalCode()}`
    ].filter(notNullish).join('\n');
}



// WEBPACK FOOTER //
// ./apps/portcall/addportcall/pilots.ts